import type { FormItemRule } from 'element-plus';

const INPUT_SPACE_EDGES = /^[\u0020\u3000]+|[\u0020\u3000]+$/g;
const INPUT_SPACES = /[\u0020\u3000]/g;
const BLANK_TEXT = /^[\p{White_Space}\p{Cf}]*$/u;
const SINGLE_LINE_CONTROLS = /[\u0009-\u000D\u0085\u2028\u2029]/;

/**
 * 提交前按字段含义选择策略；这里只处理空格，不替代业务的格式、长度和权限校验。
 * 保存、登录、查询前调用，输入过程中不调用；规范化后仍须校验同一份待提交数据。
 */
export type InputWhitespaceType =
  /** 商品/门店名称、姓名、详细地址：" Kids   Pass " → "Kids Pass"；内部全角空格保留。 */
  | 'naturalText'
  /** 员工登录账号：" cashier001 " → "cashier001"；"cashier 001" 保留内部空格，由账号规则拒绝。 */
  | 'account'
  /** 邮箱：" user@example.com " → "user@example.com"；不拼接 "a b@example.com"。 */
  | 'email'
  /** 短信/图片验证码：" 123456 " → "123456"；"123 456" 留给渠道格式校验拒绝。 */
  | 'captcha'
  /** 手工商品编码、规格编码、设备编码、实体卡号：" AB 12 " → "AB 12"；内部字符不改写。 */
  | 'systemCode'
  /** 手机号、联系电话：" 138 0000 0000 " → "13800000000"；再校验完整号码。 */
  | 'phone'
  /** 商品助记码：" vip n k " → "vipnk"；仅删半角/全角空格，大写等专用转换由调用方执行。 */
  | 'mnemonic'
  /** 金额：" 12.50 " → "12.50"；保留 "1 2" 供金额规则校验，不转数字或将空值变成 0。 */
  | 'amount'
  /** 数量、库存：" 12 " → "12"；整数、小数、正负及范围按数量字段单独校验。 */
  | 'quantity'
  /** 日期/时间：" 2026-10-29 10:30:00 " → "2026-10-29 10:30:00"；不解析日期或转换时区。 */
  | 'dateTime'
  /** 查询关键字：" Kids   Pass " → "Kids   Pass"；内部空格和搜索语法保留。 */
  | 'search'
  /** 原密码、新密码、确认密码、登录密码：原样返回；含空格时按原值校验，不自动删除。 */
  | 'password'
  /** 删除/注销等风险确认短语：原样返回；与目标文字逐字符比较，末尾多一个空格也不相等。 */
  | 'confirmation'
  /** 多行备注、描述、富文本：保留缩进、空格、换行；用 isBlankText/hasEffectiveContent 独立判空。 */
  | 'multiline'
  /** 外部同步标识、令牌/凭证、链接、文件路径：默认原样返回，按来源或专用格式处理。 */
  | 'external'
  /** 其他明确要求保持原样的字段：不清理、不合并；用于显式声明此字段无需通用处理。 */
  | 'preserve';

export type SubmitFieldPolicies<T extends object> = {
  [K in keyof T]?: InputWhitespaceType | ((value: T[K]) => T[K]);
};

/**
 * 新规范的通用首尾清理：仅半角、全角空格，不删除 NBSP 等排版空白。
 * @example trimInputSpaces('　 Kids Pass 　'); // 'Kids Pass'
 */
export function trimInputSpaces(value: string): string {
  return value.replace(INPUT_SPACE_EDGES, '');
}

/**
 * 名称、备注等自然内容独立判空，不能用于密码必填判断，也不能据此删除正文中的格式字符。
 * @example isBlankText(' \u200b　'); // true：仅空白/格式控制字符
 * @example isBlankText(' Kids Pass '); // false：有正文
 */
export function isBlankText(value: string): boolean {
  return BLANK_TEXT.test(value);
}

/**
 * 富文本必填/空内容检查：传入编辑器提取的纯文本，不能直接传 HTML 标记。
 * 有效图片等非文本内容由调用方识别，通过第二个参数传入。
 * @example hasEffectiveContent('　'); // false：无有效内容
 * @example hasEffectiveContent('', true); // true：无文字，但含有效图片等内容
 */
export function hasEffectiveContent(text: string, hasNonTextContent = false): boolean {
  return hasNonTextContent || !isBlankText(text);
}

/**
 * 单行输入框粘贴校验：检测到 Tab、换行等时，由页面阻止写入并提示修正，不静默拼接。
 * 只提供判断，不绑定输入事件；扫码结束符按扫码专用规则处理。
 * @example containsSingleLineControls('Kids\nPass'); // true
 * @example containsSingleLineControls('Kids Pass'); // false
 */
export function containsSingleLineControls(value: string): boolean {
  return SINGLE_LINE_CONTROLS.test(value);
}

/**
 * 单个字段规范化：仅在保存、登录或查询前调用；输入和输入法组字过程中保留原值。
 * 各策略适用场景见 InputWhitespaceType；返回字符串，不执行格式校验或数值解析。
 * @example normalizeInputValue(' Kids   Pass ', 'naturalText'); // 'Kids Pass'
 * @example normalizeInputValue(' 138 0000 0000 ', 'phone'); // '13800000000'
 * @example normalizeInputValue(' 1 2 ', 'quantity'); // '1 2'：仍须数量校验，不能直接提交
 */
export function normalizeInputValue(value: string, type: InputWhitespaceType): string {
  switch (type) {
    case 'naturalText':
      return trimInputSpaces(value).replace(/ {2,}/g, ' ');
    case 'phone':
      // 默认仅半角空格作为号码分组符，不扩展国家码、分机或号码接收范围。
      return trimInputSpaces(value).replace(/ /g, '');
    case 'mnemonic':
      // 其他空白/格式字符留给专用校验，不顺带做大小写、全半角字母数字转换。
      return value.replace(INPUT_SPACES, '');
    case 'account':
    case 'email':
    case 'captcha':
    case 'systemCode':
    case 'amount':
    case 'quantity':
    case 'dateTime':
    case 'search':
      return trimInputSpaces(value);
    case 'password':
    case 'confirmation':
    case 'multiline':
    case 'external':
    case 'preserve':
      return value;
  }
}

/**
 * 创建提交用的浅副本，只处理显式声明的字段，不改表单、不递归猜测字段类型。
 * 数组、多语言和嵌套对象使用回调返回新值；内置策略保留 null、数字、Date 等非字符串。
 * 调用方对该副本校验后提交同一份数据，自定义回调也不得修改传入的原值。
 * @example
 * const form = { name: ' Kids   Pass ', phone: ' 138 0000 0000 ' };
 * const payload = normalizeSubmitData(form, { name: 'naturalText', phone: 'phone' });
 * // payload 为 { name: 'Kids Pass', phone: '13800000000' }，form 保持原样。
 * // 接下来对 payload 校验，校验通过后把同一个 payload 传给保存 API。
 * @example
 * const payload = normalizeSubmitData({ mnemonic: ' vip n k ' }, {
 *   mnemonic: (value) => normalizeInputValue(value, 'mnemonic').toUpperCase(),
 * }); // { mnemonic: 'VIPNK' }：明确组合助记码的专用大写转换。
 */
export function normalizeSubmitData<T extends object>(data: T, policies: SubmitFieldPolicies<T>): T {
  const payload = { ...data };
  for (const key of Object.keys(policies) as (keyof T)[]) {
    if (!Object.prototype.hasOwnProperty.call(data, key)) {
      continue;
    }
    const policy = policies[key];
    const value = data[key];
    if (typeof policy === 'function') {
      payload[key] = policy(value);
    } else if (policy && typeof value === 'string') {
      payload[key] = normalizeInputValue(value, policy) as T[typeof key];
    }
  }
  return payload;
}

/**
 * 列表查询前处理指定的文本条件；空字符串省略，其他筛选、0、false 和空值保持原样。
 * @example
 * const params = normalizeSearchParams({ keyword: ' Kids   Pass ', orderNo: '　 ', pageNum: 1 }, ['keyword', 'orderNo']);
 * // { keyword: 'Kids   Pass', pageNum: 1 }：内部搜索空格保留，空订单号条件省略。
 */
export function normalizeSearchParams<T extends object>(data: T, fields: readonly (keyof T)[]): Partial<T> {
  const params: Partial<T> = { ...data };
  for (const key of fields) {
    if (!Object.prototype.hasOwnProperty.call(data, key) || typeof data[key] !== 'string') {
      continue;
    }
    const value = trimInputSpaces(data[key] as string);
    if (value === '') {
      delete params[key];
    } else {
      params[key] = value as T[typeof key];
    }
  }
  return params;
}

export type InputRuleTranslate = (key: string, params?: Record<string, string | number>) => string;
type NumericLimit = number | (() => number);

export interface InputRuleOptions {
  strategy: InputWhitespaceType;
  label?: string;
  required?: boolean;
  maxLength?: number;
  kind?: 'mnemonic' | 'integer' | 'amount' | 'phone' | 'email' | 'fileName';
  min?: number;
  max?: NumericLimit;
  precision?: number;
  fieldKey?: string;
  enabled?: () => boolean;
}

/**
 * 文本、联系方式、文件名、整数、金额共用的 Element Plus 规则；只校验，不改输入值。
 * 业务范围由调用方传入；金额默认两位小数，整数与金额分别校验，不自动截断。
 * @example
 * createInputRule(t, { strategy: 'naturalText', label: 'product.productName', required: true, maxLength: 60 });
 * createInputRule(t, { strategy: 'amount', kind: 'amount', min: 0, max: 999999.99, precision: 2 });
 * createInputRule(t, { strategy: 'quantity', kind: 'integer', min: 1, max: 99999 });
 */
export function createInputRule(translate: InputRuleTranslate, options: InputRuleOptions): FormItemRule & { fieldKey?: string } {
  const { strategy, label, required = false, maxLength, kind, min = 0, max = Number.MAX_SAFE_INTEGER, precision = 2, fieldKey, enabled = () => true } = options;
  return {
    required, trigger: 'blur', fieldKey,
    validator: (_rule, raw, callback) => {
      if (!enabled()) return callback();
      const value = typeof raw === 'string' ? normalizeInputValue(raw, strategy) : raw;
      const empty = value === '' || value == null || (['naturalText', 'multiline'].includes(strategy) && typeof value === 'string' && isBlankText(value));
      const fail = (key: string, params?: Record<string, string | number>) => callback(new Error(translate(key, params)));
      if (empty) return required ? callback(new Error(translate('Shared.pleaseEnter') + (label ? translate(label) : ''))) : callback();
      if (strategy !== 'multiline' && typeof value === 'string' && containsSingleLineControls(value)) return fail('Shared.inputValidation.singleLine');
      if (maxLength && String(value).length > maxLength) return fail('Shared.inputValidation.maxLength', { max: maxLength });
      if (kind === 'mnemonic' && !/^[A-Za-z0-9_-]+$/.test(String(value))) return fail('Shared.inputValidation.mnemonic');
      if (kind === 'phone' && !/^\d+$/.test(String(value))) return fail('Shared.inputValidation.phone');
      if (kind === 'email' && !/^[^@\s\p{Cf}]+@[^@\s\p{Cf}]+\.[^@\s\p{Cf}]+$/u.test(String(value))) return fail('Shared.inputValidation.email');
      if (kind === 'fileName' && (/[<>:"/\\|?*\u0000-\u001F]/.test(String(value)) || /^\.+$/.test(String(value)))) return fail('Shared.inputValidation.fileName');
      const limit = typeof max === 'function' ? max() : max;
      if (kind === 'integer' && (!/^\d+$/.test(String(value)) || !Number.isSafeInteger(Number(value)) || Number(value) < min || Number(value) > limit)) {
        return fail('Shared.inputValidation.integer', { min, max: limit });
      }
      if (kind === 'amount') {
        const pattern = precision === 0 ? /^\d+$/ : new RegExp(`^(?:\\d+(?:\\.\\d{1,${precision}})?|\\.\\d{1,${precision}})$`);
        if (!pattern.test(String(value)) || !Number.isFinite(Number(value)) || Number(value) < min || Number(value) > limit) {
          return fail('Shared.inputValidation.amount', { min, max: limit, precision });
        }
      }
      callback();
    },
  };
}

/** 只校验 YYYY-MM-DD HH:mm:ss 格式和真实日期，不使用浏览器本地时区解析。 */
export function isValidWallTime(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) return false;
  const date = new Date(`${value.replace(' ', 'T')}Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 19).replace('T', ' ') === value;
}

export interface DateTimeRangeRuleOptions {
  getRange: () => readonly [unknown, unknown];
  toUtc: (value: string) => string;
  fromUtc: (value: string) => string;
  fieldKey?: string;
  required?: boolean;
  allowEqual?: boolean;
  enabled?: () => boolean;
}

/**
 * 时间点范围：先检查真实日期，再检查起止顺序及租户时区往返；不修改表单或提交值。
 * 转换函数由页面传入 dateUTC 工具；纯日期、营业时段不使用此规则。
 */
export function createDateTimeRangeRule(translate: InputRuleTranslate, options: DateTimeRangeRuleOptions): FormItemRule & { fieldKey?: string } {
  const { getRange, toUtc, fromUtc, fieldKey, required = true, allowEqual = false, enabled = () => true } = options;
  return {
    required, trigger: 'change', fieldKey,
    validator: (_rule, _value, callback) => {
      if (!enabled()) return callback();
      const [startTime, endTime] = getRange();
      if (!required && (startTime === '' || startTime == null) && (endTime === '' || endTime == null)) return callback();
      let valid = false;
      if (isValidWallTime(startTime) && isValidWallTime(endTime)) {
        try {
          const start = toUtc(startTime);
          const end = toUtc(endTime);
          valid = isValidWallTime(start) && isValidWallTime(end) && (allowEqual ? start <= end : start < end) && fromUtc(start) === startTime && fromUtc(end) === endTime;
        } catch {
          valid = false;
        }
      }
      callback(valid ? undefined : new Error(translate('Shared.invalidDateTimeRange')));
    },
  };
}

/** 纯日期和营业时段只检查有效性，不做 UTC 转换。 */
export function isValidDateOnly(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && isValidWallTime(`${value} 00:00:00`);
}

export function isValidBusinessTime(value: unknown): value is string {
  return typeof value === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value);
}

/** 无 el-form 的内联编辑、导入和查询复用同一套同步字段规则。返回首条错误，不改输入。 */
export function getInputValidationError(translate: InputRuleTranslate, value: unknown, options: InputRuleOptions): string | undefined {
  let message: string | undefined;
  const rule = createInputRule(translate, options);
  rule.validator?.(rule, value, (error?: string | Error) => {
    message = error instanceof Error ? error.message : error;
  }, {}, {});
  return message;
}
